import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { GlobalService } from 'src/app/api/global.service';
import { TodosService, TodoStatusFilter, TodoView } from 'src/app/api/todos.service';
import { TodoEditComponent } from '../edit/todo-edit.component';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';
import { DetailsComponent as TeamMemberDetailsPage } from '../../team-members/details/details.component';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';
import { DetailsPage as ClientDetailsPage } from 'src/app/clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from 'src/app/companies/details/details.page';

@Component({
  selector: 'app-todo-table',
  templateUrl: './todo-table.component.html',
  styleUrls: ['./todo-table.component.scss'],
})
export class TodoTableComponent implements OnInit, OnChanges {
  @Input() view: TodoView = 'mine';
  @Input() status: TodoStatusFilter = 'open';
  @Input() completed_days = 1;
  @Input() member_guid = '';
  // Only To Dos linked to this contact / company (the To Do tab on their details). link_name prefills new ones.
  @Input() client_guid = '';
  @Input() company_guid = '';
  @Input() link_name = '';
  @Input() show_create = false;
  // Quick search term (applied client-side to title, description, contact/company, team members and checklist items)
  @Input() quick_search = '';
  // Column keys to hide (see availableColumns)
  @Input() hideColumns: string[] = [];
  // Compact mode (dashboard) hides the received/completed detail columns
  @Input() compact = false;
  @Input() show_paging = true;

  // Only show items for this contact / company, plus items with no contact or company assigned (used by the
  // select modals so something can't be linked across clients)
  @Input() restrict_client_guid = '';
  @Input() restrict_company_guid = '';

  private MatchesRestrictedClient(item: any): boolean {
    if (!this.restrict_client_guid && !this.restrict_company_guid) return true;
    if (!item.client_guid && !item.company_guid) return true;
    if (this.restrict_client_guid) return item.client_guid === this.restrict_client_guid;
    return item.company_guid === this.restrict_company_guid;
  }

  // ── Selection mode (used by the "select" modals, e.g. linking a time entry to an item) ──
  @Input() select_mode = false;
  @Input() max_allowed_selections = 1;
  @Input() selected_items: any[] = [];
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  IsSelected(item: any): boolean {
    return this.selected_items.some(i => i.guid === item.guid);
  }

  ToggleSelected(item: any) {
    if (!this.select_mode) return;
    if (this.IsSelected(item)) {
      this.selected_items = this.selected_items.filter(i => i.guid !== item.guid);
    } else if (this.selected_items.length < this.max_allowed_selections) {
      this.selected_items = [...this.selected_items, item];
    } else if (this.max_allowed_selections === 1) {
      // With a max of one, picking another item swaps the selection
      this.selected_items = [item];
    } else {
      return;
    }
    this.selectedItemsChange.emit(this.selected_items);
  }
  @Output() countsChanged = new EventEmitter<{ open: number; overdue: number }>();

  // todos is every To Do loaded for the current filters; filtered_todos is what the table shows (after quick search)
  private _todos: any[] = [];
  filtered_todos: any[] = [];
  get todos(): any[] { return this._todos; }
  set todos(value: any[]) {
    this._todos = value;
    this.ApplyQuickSearch();
  }
  loaded = false;
  team_members: any[] = [];
  communication_types: any[] = [];
  // Rows with a completion save in flight, or fading out of the list
  saving_guids = new Set<string>();
  leaving_guids = new Set<string>();
  private initialized = false;
  private _loadGeneration = 0;

  constructor(public todosService: TodosService, public globalService: GlobalService, private modalController: ModalController, private alertController: AlertController, private toastController: ToastController) { }

  async ngOnInit() {
    await this.RefreshLookups();
    this.SyncColumnsFromHideColumns();
    this.initialized = true;
    this.GetData();
  }

  async RefreshLookups() {
    [this.team_members, this.communication_types] = await Promise.all([
      this.todosService.GetTeamMembers(),
      this.todosService.GetCommunicationTypes(),
    ]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.initialized) return;
    // Quick search only filters what is already loaded
    // Picking an item in selection mode changes selected_items - that must not reload the table
    const no_reload = ['quick_search', 'hideColumns', 'selected_items', 'max_allowed_selections'];
    const keys = Object.keys(changes);
    if (keys.every(key => no_reload.includes(key))) {
      if (changes.quick_search || changes.hideColumns) this.ApplyQuickSearch();
    }
    else this.GetData();
  }

  async GetData() {
    const generation = ++this._loadGeneration;
    this.loaded = false;
    if (this.view === 'others' && !this.member_guid) {
      this.todos = [];
      this.loaded = true;
      return;
    }

    const data: any = await this.todosService.GetTodos(this.view, this.status, this.completed_days, this.member_guid, this.client_guid, this.company_guid);
    if (generation !== this._loadGeneration) return;

    this.todos = data.success ? this.SortTodos(data.data || []) : [];
    if (!data.success) this.globalService.presentToastMessage(data.message, false);
    this.loaded = true;
    this.EmitCounts();
  }

  // ── Columns / quick search (same pattern as the missing info table) ───────

  availableColumns: ColumnConfig[] = [
    { key: 'received_date', label: 'Date Received', visible: true },
    { key: 'title', label: 'Title', visible: true },
    { key: 'communication_type', label: 'Communication Type', visible: true },
    { key: 'linked_name', label: 'Contact / Company', visible: true },
    { key: 'received_by', label: 'Team Member Received By', visible: true },
    { key: 'assigned', label: 'Team Member Assigned', visible: true },
    { key: 'due_date', label: 'Due', visible: true },
    { key: 'checklist', label: 'Checklist', visible: true },
    { key: 'completed', label: 'Completed', visible: true },
    { key: 'completed_date', label: 'Completed Date', visible: true },
    { key: 'completed_by', label: 'Completed By', visible: true },
  ];

  // Compact mode (dashboard) starts without the received / completed detail columns
  private readonly compact_hidden_columns = ['received_date', 'communication_type', 'received_by', 'completed_date', 'completed_by'];

  isColumnVisible(key: string): boolean {
    // The contact / company column is redundant on a contact's / company's own To Do tab
    if (key === 'linked_name' && (this.client_guid || this.company_guid)) return false;
    const col = this.availableColumns.find(c => c.key === key);
    return col ? col.visible && !this.hideColumns.includes(key) : !this.hideColumns.includes(key);
  }

  get visible_column_count(): number {
    return this.availableColumns.filter(c => this.isColumnVisible(c.key)).length + 2 + (this.globalService.TimeAndBillingEnabled ? 1 : 0);
  }

  async StartTimerForTodo(todo: any) {
    if (this.globalService.timer_project?.guid === todo.guid) {
      this.globalService.StartTimer();
      return;
    }
    const start = () => {
      this.globalService.timer_project = todo;
      this.globalService.timer_project_type = 'to do';
      this.globalService.StartTimer();
      this.globalService.presentToastMessage('Timer started for: ' + todo.title, true);
    };
    if (this.globalService.timer_wall_start || this.globalService.total_seconds > 0) {
      const alert = await this.alertController.create({
        header: 'Timer Already Running',
        message: 'A timer is already running. Restart it for "' + todo.title + '"?',
        buttons: [
          { text: 'Keep Current', role: 'cancel' },
          { text: 'Restart', handler: () => { this.globalService.ResetTimer(); start(); } }
        ]
      });
      return await alert.present();
    }
    start();
  }

  async StopAndLogTodo(todo: any) {
    this.globalService.StopTimer();
    const now = new Date();
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { initial_start_time: this.globalService.GetTimerStart(now), initial_end_time: now, initial_worked_ms: this.globalService.total_seconds, reset_timer_on_save: true, todo }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.success) this.globalService.ResetTimer();
    });
    return await modal.present();
  }

  async ConfirmDiscardTimerTodo(todo: any) {
    const alert = await this.alertController.create({
      header: 'Discard Timer?',
      message: `Are you sure you want to discard the timer for "${todo.title}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Discard', role: 'destructive', handler: () => this.globalService.ResetTimer() }
      ]
    });
    await alert.present();
  }

  async OpenManualTimeEntryForTodo(todo: any) {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { todo }
    });
    return await modal.present();
  }

  private SyncColumnsFromHideColumns() {
    const hidden = this.compact ? [...this.hideColumns, ...this.compact_hidden_columns] : this.hideColumns;
    this.availableColumns.forEach(col => col.visible = !hidden.includes(col.key));
  }

  async OpenColumnPicker() {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));
    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal secondary-modal tall-modal',
      componentProps: { columns: columnsCopy },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        this.availableColumns = data.data.columns;
        this.hideColumns = this.availableColumns.filter(col => !col.visible).map(col => col.key);
      }
    });
    return await modal.present();
  }

  private ApplyQuickSearch() {
    const query = (this.quick_search || '').trim().toLowerCase();
    const todos = this._todos.filter(todo => this.MatchesRestrictedClient(todo));
    if (!query) {
      this.filtered_todos = todos;
      return;
    }
    this.filtered_todos = todos.filter(todo => {
      const values = [
        todo.title, todo.description, todo.linked_name,
        this.GetMemberName(todo.assigned_member_guid), this.GetMemberName(todo.received_by_guid), this.GetMemberName(todo.completed_by_guid),
        ...this.GetCommunicationTypes(todo).map((t: any) => t.title),
        ...(todo.checklist || []).map((i: any) => i.text),
      ];
      return values.some(value => `${value ?? ''}`.toLowerCase().includes(query));
    });
  }

  // Open items first by soonest due, then completed items most recent first
  private SortTodos(todos: any[]): any[] {
    return todos.sort((a, b) => {
      const a_done = this.todosService.IsCompleted(a), b_done = this.todosService.IsCompleted(b);
      if (a_done !== b_done) return a_done ? 1 : -1;
      if (a_done) return dayjs(b.completed_date).valueOf() - dayjs(a.completed_date).valueOf();
      // To Dos without a due date go after the dated ones
      if (!a.due_date || !b.due_date) return a.due_date ? -1 : (b.due_date ? 1 : 0);
      return dayjs(a.due_date).valueOf() - dayjs(b.due_date).valueOf();
    });
  }

  private EmitCounts() {
    // The badges only count the signed-in team member's own To Dos, whatever else the table is showing
    const me = this.globalService.GetCurrentUser()?.guid;
    const open = this.todos.filter(t => !this.todosService.IsCompleted(t) && t.assigned_member_guid === me);
    this.countsChanged.emit({ open: open.length, overdue: open.filter(t => this.todosService.IsOverdue(t)).length });
  }

  // The description is rich text (quill) - the table shows it as a one line plain text preview
  DescriptionPreview(html: string): string {
    const el = document.createElement('div');
    el.innerHTML = (html || '').replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, ' ');
    return (el.textContent || '').trim();
  }

  GetMemberName(guid: string): string {
    if (!guid) return '';
    const member = this.team_members.find(m => m.guid === guid);
    return member ? member.display_name : '';
  }

  // Communication types are stored on the To Do like tags; prefer the current title/colour if the type still exists
  GetCommunicationTypes(todo: any): any[] {
    return (todo.communication_types || []).map((type: any) => this.communication_types.find(t => t.guid === type.guid) || type);
  }

  // ── Checklist (collapsed until opened) ────────────────────────────────────

  expanded_checklists = new Set<string>();
  checklist_saving = new Set<string>();

  ToggleChecklist(todo: any) {
    if (this.expanded_checklists.has(todo.guid)) this.expanded_checklists.delete(todo.guid);
    else this.expanded_checklists.add(todo.guid);
  }

  ChecklistCheckedCount(todo: any): number {
    return (todo.checklist || []).filter((i: any) => i.checked).length;
  }

  ChecklistPercent(todo: any): number {
    const total = (todo.checklist || []).length;
    return total === 0 ? 0 : Math.round((this.ChecklistCheckedCount(todo) / total) * 100);
  }

  // Works from the freshly saved checklist so an item a teammate ticked in the meantime is not overwritten
  async ToggleChecklistItem(todo: any, item: any, event: Event) {
    const box = event.target as HTMLInputElement;
    const checked = box.checked;
    this.checklist_saving.add(todo.guid);
    let offer_complete = false;
    try {
      const latest: any = await this.todosService.GetTodo(todo.guid);
      const checklist: any[] = latest?.success ? (latest.data?.[0]?.checklist || []) : null;
      const target = checklist?.find((i: any) => i.guid === item.guid);
      if (!target) {
        box.checked = !checked;
        if (checklist) todo.checklist = checklist;
        this.globalService.presentToastMessage(checklist ? 'This checklist has changed - it has been refreshed. Please try again.' : (latest?.message || 'Could not load the checklist'), false);
        return;
      }

      if (checked) {
        target.checked = true;
        target.checked_date = dayjs().toISOString();
        target.checked_by = this.globalService.GetCurrentUser()?.display_name || '';
      } else {
        target.checked = false;
        target.checked_date = null;
        target.checked_by = null;
      }

      const saved: any = await this.todosService.UpdateChecklist(todo.guid, checklist);
      if (!saved?.success) {
        box.checked = !checked;
        this.globalService.presentToastMessage(saved?.message || 'Could not save the checklist', false);
        return;
      }
      todo.checklist = checklist;
      offer_complete = checked && !this.todosService.IsCompleted(todo) && checklist.length > 0 && checklist.every((i: any) => i.checked);
    } finally {
      this.checklist_saving.delete(todo.guid);
    }
    if (offer_complete) await this.OfferCompleteTodo(todo);
  }

  // ── Team member received by (click the cell to change it - same as the assigned column) ──

  inline_edit_received_guid: string = null;

  get team_members_with_none(): any[] {
    return [{ guid: '', display_name: 'None' }, ...this.team_members];
  }

  StartReceivedByEdit(todo: any) {
    this.inline_edit_received_guid = todo.guid;
  }

  CancelReceivedByEdit() {
    this.inline_edit_received_guid = null;
  }

  async SaveReceivedBy(todo: any, member_guid: string) {
    const previous = todo.received_by_guid;
    const next = member_guid || '';
    this.inline_edit_received_guid = null;
    if ((previous || '') === next) return;

    todo.received_by_guid = next;
    const data: any = await this.todosService.SetReceivedBy(todo, next);
    if (!data?.success) {
      todo.received_by_guid = previous;
      this.globalService.presentToastMessage(data?.message || 'Failed to update received by', false);
      return;
    }
    this.globalService.presentToastMessage('Received by updated', true);
    // In the "Received By Me" view the To Do no longer belongs here once someone else received it
    if (this.view === 'created' && next !== this.globalService.GetCurrentUser()?.guid) {
      this.todos = this.todos.filter(t => t.guid !== todo.guid);
      this.EmitCounts();
    }
  }

  // ── Assigned team member (click the cell to reassign - any team member can) ──

  inline_edit_assign_guid: string = null;

  get team_members_with_unassigned(): any[] {
    return [{ guid: '', display_name: 'Unassigned' }, ...this.team_members];
  }

  StartAssignEdit(todo: any) {
    this.inline_edit_assign_guid = todo.guid;
  }

  CancelAssignEdit() {
    this.inline_edit_assign_guid = null;
  }

  async SaveAssignment(todo: any, member_guid: string) {
    const previous = todo.assigned_member_guid;
    const next = member_guid || '';
    this.inline_edit_assign_guid = null;
    if ((previous || '') === next) return;

    todo.assigned_member_guid = next;
    const data: any = await this.todosService.AssignTodo(todo, next);
    if (!data?.success) {
      todo.assigned_member_guid = previous;
      this.globalService.presentToastMessage(data?.message || 'Failed to update assignment', false);
      return;
    }
    this.globalService.presentToastMessage('Assignment updated', true);
    // In the "mine" / "other team member" views the To Do no longer belongs here once it is reassigned
    if ((this.view === 'mine' && next !== this.globalService.GetCurrentUser()?.guid) || (this.view === 'others' && next !== this.member_guid)) {
      this.todos = this.todos.filter(t => t.guid !== todo.guid);
      this.EmitCounts();
    }
  }

  // ── Due date (click the cell to edit - any team member can) ───────────────

  inline_edit_due_guid: string = null;
  due_edit_value = '';

  StartDueEdit(todo: any) {
    this.due_edit_value = this.todosService.ToLocalInput(todo.due_date);
    this.inline_edit_due_guid = todo.guid;
  }

  CancelDueEdit() {
    this.inline_edit_due_guid = null;
  }

  // An empty value clears the due date - a To Do does not have to have one
  async SaveDueDate(todo: any, value: string = this.due_edit_value) {
    if (this.inline_edit_due_guid !== todo.guid) return;
    this.inline_edit_due_guid = null;
    const next = this.todosService.FromLocalInput(value);
    const previous = todo.due_date || null;
    if ((next || null) === previous) return;

    todo.due_date = next;
    const data: any = await this.todosService.SetDueDate(todo, next);
    if (!data?.success) {
      todo.due_date = previous;
      this.globalService.presentToastMessage(data?.message || 'Failed to update due date', false);
      return;
    }
    this.globalService.presentToastMessage('Due date updated', true);
    // In the Overdue view a To Do whose new due date is no longer past drops out of the list
    if (this.status === 'overdue' && !this.todosService.IsOverdue(todo)) this.todos = this.todos.filter(t => t.guid !== todo.guid);
    this.todos = this.SortTodos([...this.todos]);
    this.EmitCounts();
  }

  FormatDue(todo: any): string {
    if (!todo.due_date) return 'No due date';
    const due = dayjs(todo.due_date);
    if (due.isSame(dayjs(), 'day')) return `Today ${due.format('h:mm A')}`;
    if (due.isSame(dayjs().add(1, 'day'), 'day')) return `Tomorrow ${due.format('h:mm A')}`;
    return due.format('MMM D, YYYY h:mm A');
  }

  // One click from the list: stamps completed date/time and completed by, then drops the row from the Open list
  async ToggleCompleted(todo: any, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.globalService.HideTooltip();
    if (this.saving_guids.has(todo.guid)) {
      checkbox.checked = this.todosService.IsCompleted(todo);
      return;
    }

    const completed = checkbox.checked;
    this.saving_guids.add(todo.guid);
    const data: any = await this.todosService.SetCompleted(todo, completed);
    this.saving_guids.delete(todo.guid);
    if (!data?.success) {
      checkbox.checked = !completed;
      this.globalService.presentToastMessage(data?.message || 'Failed to update To Do', false);
      return;
    }

    this.AfterCompletedChange(todo, completed);
  }

  // The list side of completing / re-opening a To Do: the row leaves a list it no longer belongs to, and a
  // completion gets the undo toast
  private AfterCompletedChange(todo: any, completed: boolean) {
    if (((this.status === 'open' || this.status === 'overdue') && completed) || (this.status === 'completed' && !completed)) {
      this.leaving_guids.add(todo.guid);
      setTimeout(() => {
        this.leaving_guids.delete(todo.guid);
        this.todos = this.todos.filter(t => t.guid !== todo.guid);
        this.EmitCounts();
      }, 350);
    } else {
      this.todos = this.SortTodos([...this.todos]);
      this.EmitCounts();
    }
    if (completed) this.PresentUndoToast(todo);
  }

  // Everything on the checklist is ticked - offer to complete the To Do too
  private async OfferCompleteTodo(todo: any) {
    const alert = await this.alertController.create({
      header: 'Checklist Complete',
      message: 'Everything on this checklist is checked off. Would you like to mark this To Do as completed?',
      buttons: [
        { text: 'Not Yet', role: 'cancel' },
        {
          text: 'Mark Completed',
          handler: async () => {
            this.saving_guids.add(todo.guid);
            const data: any = await this.todosService.SetCompleted(todo, true);
            this.saving_guids.delete(todo.guid);
            if (!data?.success) {
              this.globalService.presentToastMessage(data?.message || 'Failed to update To Do', false);
              return;
            }
            this.AfterCompletedChange(todo, true);
          }
        }
      ]
    });
    await alert.present();
  }

  private async PresentUndoToast(todo: any) {
    const toast = await this.toastController.create({
      message: `Completed "${todo.title}"`,
      position: 'bottom',
      duration: 5000,
      color: 'dark',
      buttons: [{ text: 'Undo', role: 'undo' }],
    });
    await toast.present();
    const { role } = await toast.onDidDismiss();
    if (role !== 'undo') return;

    const data: any = await this.todosService.SetCompleted(todo, false);
    if (!data?.success) {
      this.globalService.presentToastMessage(data?.message || 'Failed to undo', false);
      return;
    }
    this.RefreshOneTodo(todo.guid);
  }

  // Whether a To Do belongs in the table for its current filters - the same rules the list request applies
  private MatchesCurrentFilters(todo: any): boolean {
    const user = this.globalService.GetCurrentUser();
    const assigned_to = this.view === 'mine' ? user?.guid : (this.view === 'others' ? this.member_guid : '');
    const received_by = this.view === 'created' ? user?.guid : '';
    if (assigned_to && todo.assigned_member_guid !== assigned_to) return false;
    if (received_by && todo.received_by_guid !== received_by) return false;
    if (this.client_guid && todo.client_guid !== this.client_guid) return false;
    if (this.company_guid && todo.company_guid !== this.company_guid) return false;
    const completed = this.todosService.IsCompleted(todo);
    if ((this.status === 'open' || this.status === 'overdue') && completed) return false;
    if (this.status === 'overdue' && !this.todosService.IsOverdue(todo)) return false;
    if (this.status === 'completed' && !completed) return false;
    if (completed) {
      const completed_after = dayjs().subtract(this.completed_days - 1, 'day').startOf('day');
      if (dayjs(todo.completed_date).isBefore(completed_after)) return false;
    }
    return true;
  }

  // After a To Do was created or changed: reloads just that one and updates the table - the row is replaced, added if it now
  // belongs here, or dropped if it no longer does - instead of reloading the whole list
  async RefreshOneTodo(guid: string) {
    if (!guid) { this.GetData(); return; }
    try {
      const data: any = await this.todosService.GetTodo(guid);
      const fresh = data?.success ? data.data?.[0] : null;
      if (!fresh) throw new Error('To Do not found');
      const others = this._todos.filter(t => t.guid !== guid);
      const existing = this._todos.find(t => t.guid === guid);
      if (this.MatchesCurrentFilters(fresh)) {
        // Updated in place when it was already a row, so anything the row holds is kept
        const row = existing ? Object.assign(existing, fresh) : fresh;
        this.todos = this.SortTodos([...others, row]);
      } else {
        this.todos = this.SortTodos(others);
      }
      this.EmitCounts();
    } catch (e) {
      // Couldn't update just that row - fall back to reloading the list
      this.GetData();
    }
  }

  // The Client column is redundant on a contact's / company's own To Do tab
  get hide_client_column(): boolean {
    return !!(this.client_guid || this.company_guid);
  }

  // Opens the linked contact / company straight onto its To Do tab
  async OpenLinkedDetails(todo: any, event: Event) {
    event.stopPropagation();
    const is_company = !!todo.company_guid;
    const modal = await this.modalController.create({
      component: is_company ? CompanyDetailsPage : ClientDetailsPage,
      cssClass: 'large-modal',
      componentProps: is_company ? { company: { guid: todo.company_guid }, start_tab: 'todos' } : { client: { guid: todo.client_guid }, start_tab: 'todos' },
      backdropDismiss: false,
    });
    return await modal.present();
  }

  // Mirrors the missing info table's "Item Recorded By" - opens the team member's details
  async OpenTeamMemberDetails(guid: string, event: Event) {
    event.stopPropagation();
    if (!guid) return;
    const modal = await this.modalController.create({
      component: TeamMemberDetailsPage,
      cssClass: 'large-modal',
      componentProps: { team_member: { guid } },
      backdropDismiss: false
    });
    await modal.present();
  }

  async OpenEdit(todo: any = null) {
    const modal = await this.modalController.create({
      component: TodoEditComponent,
      cssClass: 'large-modal',
      componentProps: {
        todo,
        // The selected team member in the Other Team Member view, otherwise the current user - never left empty
        default_assigned_guid: (this.view === 'others' && this.member_guid) ? this.member_guid : (this.globalService.GetCurrentUser()?.guid || ''),
        default_client_guid: this.client_guid,
        default_company_guid: this.company_guid,
        default_linked_name: this.link_name,
      },
    });
    modal.onDidDismiss().then((result: any) => {
      if (!result.data?.saved) return;
      const guid = todo?.guid || result.data.todo?.guid;
      if (guid) this.RefreshOneTodo(guid);
      else this.GetData();
    });
    return await modal.present();
  }

  async Delete(todo: any) {
    const alert = await this.alertController.create({
      header: 'Delete To Do',
      message: `Delete "${todo.title}"? This cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          handler: async () => {
            const data: any = await this.todosService.DeleteTodo(todo);
            this.globalService.presentToastMessage(data.success ? 'To Do deleted' : data.message, data.success);
            if (data.success) {
              this.todos = this.todos.filter(t => t.guid !== todo.guid);
              this.EmitCounts();
            }
          }
        }
      ]
    });
    await alert.present();
  }
}
